/**
 * Agent providers beyond Claude Code and Codex (ADR 0049, R-PL-2).
 *
 * Local CLIs that speak the Agent Client Protocol (ACP) run through one generic adapter on the
 * machine; each is a data entry, an AcpAgentDefinition, not code. Cloud agents (Devin, Cursor
 * Cloud Agents, GitHub Copilot's cloud agent, Jules, any A2A agent) run on the vendor's machines
 * and are driven from the server; Codex cloud, which has no public API, through a machine-side
 * bridge over the `codex cloud` CLI. A cloud run is a Session with `machineId: 'hub'` (or the
 * bridging machine), so Sessions, Missions, Needs you and the phone show it with no new pages.
 *
 * This file is the contract: definitions (ACP_AGENTS, the data the generic ACP adapter in
 * agents/agent-ts/src/adapters/agent/acp/ starts), the ACP runtime summary, cloud providers with
 * their honest capability matrix, connections and runs. Browser-safe.
 */
import type { Id, Ts, UiErrorShape } from '../index.ts';
import type { AgentCapability, AgentCapabilityInfo, AgentKind, AgentSupport } from './adapters.ts';
import type { CanonicalSessionState } from './missions.ts';
import { ulid } from './ids.ts';

// ---------------------------------------------------------------------------
// ACP: local coding CLIs
// ---------------------------------------------------------------------------

/** The stable ACP protocol version Orgus speaks. v2 is a draft and refused until it is stable. */
export const ACP_PROTOCOL_VERSION = 1;

/** How the agent signs in when ACP answers auth_required: its own method, or a terminal at that machine. */
export type AcpSignIn = 'agent' | 'terminal' | 'none';

/** One way to start the agent in ACP mode, for a range of its versions. */
export interface AcpLaunch {
  /** 'gemini', '--acp'. The first entry is the program, looked up on PATH. */
  argv: string[];
  /** Semver range this launch applies to ('>=0.9.0'); absent: every version. */
  versions?: string;
}

/**
 * An ACP agent as data. Adding an ACP agent is one entry: no code. `capabilities` is the verified
 * static row; what `initialize` reports overlays it at runtime.
 */
export interface AcpAgentDefinition {
  /** The agent kind token: 'gemini-cli', 'cursor-cli', 'copilot-cli', 'opencode', 'qwen-code'. */
  kind: string;
  label: string;
  vendor: string;
  /** An icon name from @orgus/ui/icons. */
  icon?: string;
  launches: AcpLaunch[];
  /** Arguments that print the version ('--version'). */
  versionArgs: string[];
  signIn: AcpSignIn;
  /** The vendor's documentation page for the ACP mode. */
  docs: string;
  capabilities: Partial<Record<AgentCapability, AgentCapabilityInfo>>;
  /** Permission modes Orgus may set (session config category 'mode'); never a mode that skips approvals. */
  permissionModes?: string[];
  /**
   * Variables every launch sets: vendor switches for a decision Orgus already made, such as trusting
   * the checkout it placed the session in.
   */
  launchEnv?: Record<string, string>;
  /** Where it can export OpenTelemetry to a file Orgus tails (no OTLP receiver: no listening port). */
  otelFile?: { env: Record<string, string> };
  /**
   * Settings every launch layers on top of the CLI's own system settings: Orgus writes the merged
   * file under its home and names it in `env`. `defaults` is the CLI's system settings path per
   * platform, read (never written) so a machine's own system settings still apply. Additive, 20 Oct 2026.
   */
  systemSettings?: { env: string; defaults: Partial<Record<'win32' | 'darwin' | 'linux', string>>; settings: Record<string, unknown> };
  /**
   * Where the CLI writes its own session files, read-only, so sessions started outside Orgus are
   * listed too (ACP cannot list them). `format` names the reader in the agent; the folder is
   * `<$homeEnv or the user's home>/<dir>`. Additive, 20 Oct 2026.
   */
  sessionFiles?: AcpSessionFiles;
  /** Through a bridge package rather than the CLI itself (Codex through codex-acp). */
  bridge?: string;
  /**
   * The agent's id in the ACP Registry (acp-registry.ts), when it lists it: the registry's name,
   * version, description and distribution merge with this entry, which then carries only what the
   * registry does not (permission-mode mapping, OpenTelemetry file settings, verified caveats).
   * Registry ids are not Orgus kinds ('gemini' is kind 'gemini-cli'). Additive, 19 Oct 2026.
   */
  registryId?: string;
}

/** An ACP agent's own session files (AcpAgentDefinition.sessionFiles). */
export interface AcpSessionFiles {
  /** The reader: 'gemini-chats' is Gemini CLI's tmp/<project>/chats/session-*.jsonl. */
  format: 'gemini-chats';
  /** The CLI's folder under the home: '.gemini'. One path segment. */
  dir: string;
  /** The variable that moves the home ('GEMINI_CLI_HOME'). */
  homeEnv?: string;
}

/** What `initialize` reported, kept on the runtime record (AgentRuntime.acp). */
export interface AcpRuntimeSummary {
  protocolVersion: number;
  agentInfo?: { name: string; version?: string };
  loadSession: boolean;
  sessions: { list: boolean; resume: boolean; close: boolean };
  embeddedContext: boolean;
  mcp: { http: boolean; sse: boolean };
  authMethods: { id: string; name: string; type: 'agent' | 'terminal' }[];
  /** Whether the agent answered auth_required at the last check. */
  signedIn: boolean | null;
  checkedAt: Ts;
  /**
   * What the agent offers to set, from its session setup answer (`configOptions` of `session/new`,
   * then `config_option_update`): per category, the option and its values in the agent's order.
   * Absent until the agent's first session on this machine, and from agents older than 19 Oct 2026.
   * Orgus matches no model names: routing reads these values. Additive.
   */
  config?: AcpAdvertisedConfig;
}

/** The session config categories Orgus reads (ACP v1 `SessionConfigOptionCategory`; model_config and custom ones are ignored). */
export type AcpConfigCategory = 'model' | 'mode' | 'thought_level';
export const ACP_CONFIG_CATEGORIES: readonly AcpConfigCategory[] = ['model', 'mode', 'thought_level'];

/** One advertised value: `value` is what Orgus sets, `name` what a person reads. */
export interface AcpConfigValue {
  value: string;
  name: string;
}

/** One select option the agent advertises, bounded (ACP_CONFIG_MAX_VALUES values). */
export interface AcpAdvertisedOption {
  /** The option id `session/set_config_option` takes; for `via: 'legacy'`, the legacy list's name. */
  id: string;
  /** The value the session starts with (the agent's default). */
  current: string | null;
  /** In the agent's order (groups flattened): the order a client shows and breaks ties with. */
  values: AcpConfigValue[];
  /** From the earlier session API (`models.availableModels`, `modes.availableModes`): set with session/set_model or session/set_mode. */
  via?: 'legacy';
}

export type AcpAdvertisedConfig = Partial<Record<AcpConfigCategory, AcpAdvertisedOption>>;

/** Bounds a hub keeps of advertised config: values per option, and characters per value and name. */
export const ACP_CONFIG_MAX_VALUES = 64;
export const ACP_CONFIG_VALUE_MAX = 200;
export const ACP_CONFIG_NAME_MAX = 120;

/** The values an agent advertises in a category ([] when it advertised none). */
export function advertisedValues(summary: Pick<AcpRuntimeSummary, 'config'> | null | undefined, category: AcpConfigCategory): string[] {
  return summary?.config?.[category]?.values.map((v) => v.value) ?? [];
}

export type AcpStopReason = 'end_turn' | 'max_tokens' | 'max_turn_requests' | 'refusal' | 'cancelled';

/** Canonical state after a turn ends with each stop reason (a process exit without a reply is 'failed'). */
export const ACP_STOP_STATE: Record<AcpStopReason, CanonicalSessionState> = {
  end_turn: 'idle',
  max_tokens: 'idle',
  max_turn_requests: 'idle',
  refusal: 'idle',
  cancelled: 'stopped',
};

export type AcpToolKind = 'read' | 'edit' | 'delete' | 'move' | 'search' | 'execute' | 'think' | 'fetch' | 'switch_mode' | 'other';

/** The tool name an approval shows for each ACP tool kind (as Claude Code names them). */
export const ACP_TOOL_NAME: Record<AcpToolKind, string> = {
  read: 'Read',
  edit: 'Edit',
  delete: 'Delete',
  move: 'Move',
  search: 'Grep',
  execute: 'Bash',
  think: 'Think',
  fetch: 'WebFetch',
  switch_mode: 'SwitchMode',
  other: 'Tool',
};

export type AcpPermissionOptionKind = 'allow_once' | 'allow_always' | 'reject_once' | 'reject_always';
/** Orgus only ever answers with these, so an agent never remembers an allow Orgus does not control. */
export const ACP_ANSWER_KINDS: readonly AcpPermissionOptionKind[] = ['allow_once', 'reject_once'];

/** session ext for an ACP session. */
export interface AcpSessionExt {
  acpVersion: number;
  agentInfo?: { name: string; version?: string };
  /** The last stop reason, as the agent sent it. */
  native?: AcpStopReason;
  /** Context used and window size, and the optional cumulative cost, from usage_update. */
  usage?: { used: number; size: number; cost?: { amount: number; currency: string } };
  /** The agent's plan (the `plan` update), newest version, bounded. */
  plan?: AcpPlanEntry[];
  /** Permission requests waiting for an answer right now. */
  pendingApprovals?: number;
  /** A turn ended short of its goal (refusal, max_tokens, max_turn_requests), in words. */
  warning?: string;
  /** The agent answered auth_required: who has to sign in, and how. */
  signIn?: { needed: true; methods: string[] };
}

export interface AcpPlanEntry {
  content: string;
  priority: 'high' | 'medium' | 'low';
  status: 'pending' | 'in_progress' | 'completed';
}

// ---------------------------------------------------------------------------
// ACP agent definitions (data: adding an agent is one entry)
// ---------------------------------------------------------------------------

/** The placeholder in AcpAgentDefinition.otelFile.env replaced by the per-run file Orgus tails. */
export const ACP_OTEL_FILE_PLACEHOLDER = '{file}';

const ACP_REGISTRY_DOCS = 'https://agentclientprotocol.com/get-started/agents';
const acpYes = (note: string): AgentCapabilityInfo => ({ support: 'yes', note });
const acpPartial = (note: string): AgentCapabilityInfo => ({ support: 'partial', note });

/**
 * The overlay: Orgus-only facts for ACP agents, keyed by registry id (ADR 0049 decision 5).
 * Every other agent the ACP Registry lists becomes a known kind through module 'acp-registry'
 * (registryDefinitions), with no entry here. Each entry here is an ACP agent Orgus starts, in the order pickers show them. Launch commands are from each
 * vendor's ACP documentation and the official ACP registry (ADR 0049 Research, 17 Oct 2026).
 * Claude Code is not here: it keeps its native adapter (hooks, subagents, workflows, exact tokens).
 */
export const ACP_AGENTS: readonly AcpAgentDefinition[] = [
  {
    kind: 'gemini-cli',
    registryId: 'gemini',
    label: 'Gemini CLI',
    vendor: 'Google',
    // Gemini CLI 0.58 has --acp and keeps --experimental-acp as a deprecated alias; older versions only have the latter.
    launches: [{ argv: ['gemini', '--acp'], versions: '>=0.58.0' }, { argv: ['gemini', '--experimental-acp'] }],
    versionArgs: ['--version'],
    signIn: 'agent',
    docs: 'https://github.com/google-gemini/gemini-cli/blob/main/docs/cli/acp-mode.md',
    capabilities: {
      discover: acpYes('Every Gemini CLI session on the machine, from the chat files Gemini CLI writes (read-only), including sessions started in its own terminal.'),
      tokens: acpYes('Input, cached input and output tokens of every reply from the chat files Gemini CLI writes, and context use from ACP. Thoughts count as output.'),
      permissions: acpPartial('Gemini CLI modes default, auto edit and plan. Orgus never starts it in yolo mode.'),
    },
    permissionModes: ['default', 'autoEdit', 'auto_edit', 'plan'],
    // In a folder it does not trust, Gemini CLI refuses Accept edits (autoEdit), so an unattended mission
    // would ask a person before every edit. Orgus only starts it in a checkout of a project the owner
    // added, so it trusts that workspace for the launch (live test, 19 Oct 2026: a fresh worktree then
    // takes autoEdit). https://geminicli.com/docs/cli/trusted-folders/#headless-and-automated-environments
    launchEnv: { GEMINI_CLI_TRUST_WORKSPACE: 'true' },
    // Orgus starts it hidden, with no console: node-pty's ConPTY helper then dies on every shell
    // command with "AttachConsole failed" (Windows, 20 Oct 2026). Plain child processes run them.
    systemSettings: {
      env: 'GEMINI_CLI_SYSTEM_SETTINGS_PATH',
      defaults: { win32: 'C:\\ProgramData\\gemini-cli\\settings.json', darwin: '/Library/Application Support/GeminiCli/settings.json', linux: '/etc/gemini-cli/settings.json' },
      settings: { tools: { shell: { enableInteractiveShell: false } } },
    },
    otelFile: {
      env: {
        GEMINI_TELEMETRY_ENABLED: 'true',
        GEMINI_TELEMETRY_TARGET: 'local',
        GEMINI_TELEMETRY_OUTFILE: ACP_OTEL_FILE_PLACEHOLDER,
        GEMINI_TELEMETRY_LOG_PROMPTS: 'false',
      },
    },
    // Sessions a person starts in a terminal are listed from Gemini CLI's chat files (ADR 0049).
    sessionFiles: { format: 'gemini-chats', dir: '.gemini', homeEnv: 'GEMINI_CLI_HOME' },
  },
  {
    kind: 'copilot-cli',
    registryId: 'github-copilot-cli',
    label: 'GitHub Copilot CLI',
    vendor: 'GitHub',
    launches: [{ argv: ['copilot', '--acp'] }],
    versionArgs: ['--version'],
    signIn: 'terminal',
    docs: 'https://docs.github.com/en/copilot/reference/cli-command-reference',
    capabilities: {
      'launch.headless': acpYes('Runs copilot --acp. Its ACP mode is in public preview, and Copilot syncs sessions to GitHub by default.'),
    },
  },
  {
    kind: 'cursor-cli',
    registryId: 'cursor',
    label: 'Cursor CLI',
    vendor: 'Cursor',
    launches: [{ argv: ['cursor-agent', 'acp'] }],
    versionArgs: ['--version'],
    signIn: 'terminal',
    docs: 'https://cursor.com/docs/cli/acp',
    capabilities: {
      'launch.headless': acpYes('Runs cursor-agent acp. Cursor documents its ACP mode as hidden, so it may change without notice.'),
    },
  },
  {
    kind: 'opencode',
    registryId: 'opencode',
    label: 'OpenCode',
    vendor: 'SST',
    launches: [{ argv: ['opencode', 'acp'] }],
    versionArgs: ['--version'],
    signIn: 'terminal',
    docs: 'https://opencode.ai/docs/acp/',
    capabilities: {
      'launch.headless': acpYes('Runs opencode acp. OpenCode opens its own server on 127.0.0.1 inside its process: that port is the agent\'s, not Orgus\'s.'),
    },
  },
  {
    kind: 'qwen-code',
    registryId: 'qwen-code',
    label: 'Qwen Code',
    vendor: 'Alibaba',
    launches: [{ argv: ['qwen', '--acp'] }],
    versionArgs: ['--version'],
    signIn: 'agent',
    docs: 'https://qwenlm.github.io/qwen-code-docs/en/users/features/headless/',
    capabilities: {
      approvals: acpPartial('Qwen Code skips permission requests in some ACP sessions (QwenLM/qwen-code#11887): until that is fixed, a tool may run without asking Orgus. Use plan mode or a project policy for anything risky.'),
    },
  },
  {
    kind: 'goose',
    registryId: 'goose',
    label: 'goose',
    vendor: 'Block',
    launches: [{ argv: ['goose', 'acp'] }],
    versionArgs: ['--version'],
    signIn: 'terminal',
    docs: ACP_REGISTRY_DOCS,
    capabilities: {},
  },
  {
    kind: 'kimi',
    registryId: 'kimi',
    label: 'Kimi CLI',
    vendor: 'Moonshot AI',
    launches: [{ argv: ['kimi', 'acp'] }],
    versionArgs: ['--version'],
    signIn: 'terminal',
    docs: ACP_REGISTRY_DOCS,
    capabilities: {},
  },
  {
    kind: 'cline',
    registryId: 'cline',
    label: 'Cline',
    vendor: 'Cline',
    launches: [{ argv: ['cline', '--acp'] }],
    versionArgs: ['--version'],
    signIn: 'terminal',
    docs: ACP_REGISTRY_DOCS,
    capabilities: {},
  },
  {
    kind: 'junie',
    registryId: 'junie',
    label: 'Junie',
    vendor: 'JetBrains',
    launches: [{ argv: ['junie', '--acp=true'] }],
    versionArgs: ['--version'],
    signIn: 'terminal',
    docs: ACP_REGISTRY_DOCS,
    capabilities: {},
  },
  {
    kind: 'codex-acp',
    registryId: 'codex-acp',
    label: 'Codex (ACP)',
    vendor: 'OpenAI',
    launches: [{ argv: ['codex-acp'] }],
    versionArgs: ['--version'],
    signIn: 'agent',
    docs: ACP_REGISTRY_DOCS,
    bridge: '@agentclientprotocol/codex-acp',
    capabilities: {
      'launch.headless': acpYes('Runs Codex through the codex-acp bridge (npm install -g @agentclientprotocol/codex-acp), which gives Codex remote approvals. Codex sessions started elsewhere still show through the native Codex watcher.'),
    },
  },
];

/** The ACP definition of a kind, or null for a kind that is not an ACP agent. */
export function acpAgent(kind: string | null | undefined): AcpAgentDefinition | null {
  return (kind && ACP_AGENTS.find((d) => d.kind === kind)) || null;
}

/**
 * Whether Orgus may start `kind` in an Orgus permission mode: an ACP agent never runs in a mode
 * that skips approvals (Orgus answers its permission requests), so 'bypassPermissions' rules it
 * out; every adapter maps or refuses the other modes itself.
 */
export function acceptsPermissionMode(kind: string, mode: string | null | undefined): boolean {
  return !(mode === 'bypassPermissions' && acpAgent(kind));
}

/** "0.58.1" as [0, 58, 1]; pre-release and build suffixes are ignored. null when not a version. */
function versionParts(v: string): [number, number, number] | null {
  const m = /^v?(\d+)(?:\.(\d+))?(?:\.(\d+))?/.exec(v.trim());
  if (!m) return null;
  return [Number(m[1]), Number(m[2] ?? 0), Number(m[3] ?? 0)];
}

function compareVersions(a: [number, number, number], b: [number, number, number]): number {
  for (let i = 0; i < 3; i++) if (a[i] !== b[i]) return (a[i] as number) - (b[i] as number);
  return 0;
}

/**
 * Does a version satisfy a range? Ranges are space-separated comparators (">=0.9.0 <2.0.0";
 * operators >=, >, <=, <, = or none). An unreadable version or comparator never matches.
 */
export function versionSatisfies(version: string, range: string): boolean {
  const v = versionParts(version);
  if (!v) return false;
  const parts = range.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return true;
  for (const p of parts) {
    const m = /^(>=|<=|>|<|=)?(.+)$/.exec(p);
    const want = m ? versionParts(m[2] as string) : null;
    if (!m || !want) return false;
    const c = compareVersions(v, want);
    const op = m[1] ?? '=';
    const ok = op === '>=' ? c >= 0 : op === '>' ? c > 0 : op === '<=' ? c <= 0 : op === '<' ? c < 0 : c === 0;
    if (!ok) return false;
  }
  return true;
}

/**
 * The argv that starts a definition in ACP mode for an installed version: the first launch whose
 * range the version satisfies, else the first launch without a range, else the last one.
 */
export function acpLaunchFor(def: AcpAgentDefinition, version: string | null): AcpLaunch {
  const ranged = version ? def.launches.find((l) => l.versions && versionSatisfies(version, l.versions)) : undefined;
  return ranged ?? def.launches.find((l) => !l.versions) ?? (def.launches[def.launches.length - 1] as AcpLaunch);
}

// ---------------------------------------------------------------------------
// Cloud agents
// ---------------------------------------------------------------------------

export type CloudAgentProviderId = 'devin' | 'cursor-cloud' | 'copilot-agent' | 'jules' | 'a2a' | 'codex-cloud';
export const CLOUD_AGENT_PROVIDER_IDS: readonly CloudAgentProviderId[] = ['devin', 'cursor-cloud', 'copilot-agent', 'jules', 'a2a', 'codex-cloud'];

export type CloudAgentCapability = 'create' | 'status' | 'message' | 'plan.approve' | 'cancel' | 'webhooks' | 'usage' | 'budget' | 'pull-request';
export const CLOUD_AGENT_CAPABILITY_ORDER: readonly CloudAgentCapability[] = ['create', 'status', 'message', 'plan.approve', 'cancel', 'webhooks', 'usage', 'budget', 'pull-request'];
export const CLOUD_AGENT_CAPABILITY_LABEL: Record<CloudAgentCapability, string> = {
  create: 'Start a run',
  status: 'Follow its state',
  message: 'Send a message',
  'plan.approve': 'Approve its plan from Orgus',
  cancel: 'Cancel',
  webhooks: 'Live updates by webhook',
  usage: 'Usage and cost',
  budget: 'Budget enforced by the vendor',
  'pull-request': 'Ends in a pull request',
};

export interface CloudAgentCapabilityInfo {
  support: AgentSupport;
  note?: string;
}

/** The credential each provider takes, narrowest first. */
export type CloudAgentCredential = 'service-key' | 'repo-key' | 'user-token' | 'api-key' | 'chatgpt-login' | 'a2a-auth';

export interface CloudAgentProviderInfo {
  id: CloudAgentProviderId;
  label: string;
  vendor: string;
  /** 'server': driven from the hub or control plane. 'machine': a bridge on one of the user's machines. */
  where: 'server' | 'machine';
  credential: CloudAgentCredential;
  /** 'launching-person': the run acts as whoever launched it, and the console says so. */
  runsAs: 'service' | 'launching-person' | 'machine-owner';
  /** What the console says about who the run acts as, or why a capability is partial. */
  note?: string;
  capabilities: Record<CloudAgentCapability, CloudAgentCapabilityInfo>;
}

const Y: CloudAgentCapabilityInfo = { support: 'yes' };
const N = (note: string): CloudAgentCapabilityInfo => ({ support: 'no', note });
const P = (note: string): CloudAgentCapabilityInfo => ({ support: 'partial', note });

/** The honest matrix (ADR 0049 Research, 17 Oct 2026). Vendor APIs are young: each provider pins a version. */
export const CLOUD_AGENT_PROVIDERS: Readonly<Record<CloudAgentProviderId, CloudAgentProviderInfo>> = {
  devin: {
    id: 'devin',
    label: 'Devin',
    vendor: 'Cognition',
    where: 'server',
    credential: 'service-key',
    runsAs: 'service',
    capabilities: {
      create: Y,
      status: Y,
      message: Y,
      'plan.approve': P('Safe-mode approvals show in the Inbox with a link to Devin until Devin offers an approve endpoint.'),
      cancel: Y,
      webhooks: N('Devin has no status webhook: Orgus polls.'),
      usage: Y,
      budget: Y,
      'pull-request': Y,
    },
  },
  'cursor-cloud': {
    id: 'cursor-cloud',
    label: 'Cursor Cloud Agents',
    vendor: 'Cursor',
    where: 'server',
    credential: 'repo-key',
    runsAs: 'service',
    capabilities: {
      create: Y,
      status: Y,
      message: P('One active run per agent: a message while it runs waits for the run to end.'),
      'plan.approve': N('Cursor has no plan approval endpoint: plan mode stops for a message instead.'),
      cancel: Y,
      webhooks: N('Cursor webhooks are not available yet: Orgus polls.'),
      usage: Y,
      budget: N('Orgus enforces the budget by cancelling the run at the limit.'),
      'pull-request': Y,
    },
  },
  'copilot-agent': {
    id: 'copilot-agent',
    label: 'GitHub Copilot cloud agent',
    vendor: 'GitHub',
    where: 'server',
    credential: 'user-token',
    runsAs: 'launching-person',
    note: 'Runs as you on GitHub: Copilot accepts only the token of the person who starts it.',
    capabilities: {
      create: Y,
      status: Y,
      message: N('Steer it in GitHub: Copilot has no message API.'),
      'plan.approve': N('Review happens on the pull request.'),
      cancel: P('Cancel the task in GitHub; Orgus stops following it.'),
      webhooks: P('Pull request events arrive through the Orgus GitHub App; task state is polled.'),
      usage: Y,
      budget: N('Orgus enforces the budget by stopping at the limit.'),
      'pull-request': Y,
    },
  },
  jules: {
    id: 'jules',
    label: 'Jules',
    vendor: 'Google',
    where: 'server',
    credential: 'api-key',
    runsAs: 'service',
    note: 'Jules keys cannot be scoped: use one connection per project and rotate often.',
    capabilities: {
      create: Y,
      status: Y,
      message: Y,
      'plan.approve': Y,
      cancel: N('Jules documents no cancel endpoint: Orgus stops following the session.'),
      webhooks: N('Jules has no webhooks: Orgus polls.'),
      usage: P('Jules reports quotas, not money: Orgus counts tasks.'),
      budget: N('Jules plans limit tasks a day, not spend.'),
      'pull-request': Y,
    },
  },
  a2a: {
    id: 'a2a',
    label: 'Any A2A agent',
    vendor: 'A2A protocol',
    where: 'server',
    credential: 'a2a-auth',
    runsAs: 'service',
    capabilities: {
      create: Y,
      status: Y,
      message: Y,
      'plan.approve': P('Input-required tasks wait for a person in Orgus.'),
      cancel: Y,
      webhooks: P('Push notifications where the agent offers them, else polling.'),
      usage: N('A2A has no usage fields.'),
      budget: N('Orgus enforces the budget by cancelling at the limit.'),
      'pull-request': P('Only when the agent reports a branch or pull request.'),
    },
  },
  'codex-cloud': {
    id: 'codex-cloud',
    label: 'Codex cloud',
    vendor: 'OpenAI',
    where: 'machine',
    credential: 'chatgpt-login',
    runsAs: 'machine-owner',
    note: "OpenAI has no public API for Codex cloud: Orgus drives the codex cloud command on a machine signed in to ChatGPT, so the run acts as that machine owner's ChatGPT account.",
    capabilities: {
      create: P('Needs an online machine with Codex signed in to ChatGPT.'),
      status: P('Polled through the codex cloud command on that machine.'),
      message: N('Follow-ups happen in ChatGPT.'),
      'plan.approve': N('Review happens in ChatGPT.'),
      cancel: N('Cancel it in ChatGPT.'),
      webhooks: N('Polled through the command line.'),
      usage: N('Codex cloud reports no usage.'),
      budget: N('Orgus cannot limit Codex cloud spend.'),
      'pull-request': P('The diff comes back to apply; Orgus opens the branch.'),
    },
  },
};

/** Who a provider's connection is with, and its credential by reference (never the value). */
export interface CloudAgentConnection {
  /** cac_<ulid> */
  id: Id;
  provider: CloudAgentProviderId;
  title: string;
  projectIds: Id[];
  /** A secrets-module id or a sealed D1 row; never sent to a node. Absent for user-token providers (per person). */
  secretRef?: string;
  /**
   * A2A: the agent card URL. Devin: the organization id. The server calls a URL here, so it is
   * held to the same rules as a CI connection's baseUrl: https only, no credentials in it,
   * redirects not followed, and never a loopback, link-local, private or metadata address on the
   * hosted control plane.
   */
  endpoint?: string;
  /** codex-cloud: the machine that bridges, and the Codex environment id. */
  machineId?: Id;
  environmentId?: string;
  status: 'connected' | 'error' | 'paused';
  lastError?: UiErrorShape;
  createdBy: string;
  createdAt: Ts;
}

export function newCloudAgentConnectionId(now?: number): Id {
  return `cac_${ulid(now)}`;
}

/** A spend cap passed down where the vendor enforces it (Devin max_acu_limit), else enforced by cancelling. */
export interface CloudBudget {
  maxUsd?: number;
  maxAcu?: number;
}

/** Usage in the provider's own unit, priced by the costs module into one currency. */
export interface CloudUsage {
  unit: 'acu' | 'ai-credits' | 'premium-requests' | 'tokens' | 'tasks';
  amount: number;
  /** Cost the provider reported, when it does. */
  costUsd?: number;
}

export interface CloudRun {
  /** The provider's run id, namespaced: 'devin:abc'. */
  id: string;
  provider: CloudAgentProviderId;
  connectionId: Id;
  projectId: Id;
  missionId: Id | null;
  /** The Session that shows this run (machineId 'hub', or the bridge machine). */
  sessionId: Id;
  /** The provider's own state name. */
  nativeState: string;
  state: CanonicalSessionState;
  repo: string;
  baseRef: string;
  branch?: string;
  pullRequests: { url: string; number?: number; state?: 'open' | 'closed' | 'merged' }[];
  usage?: CloudUsage;
  url: string;
  /** Who started it; for 'launching-person' providers the run acts as them. */
  createdBy: string;
  createdAt: Ts;
  updatedAt: Ts;
  problem?: UiErrorShape;
}

export interface CloudRunRef {
  id: string;
  url: string;
}

/** POST /api/agents-cloud/runs */
export interface CloudRunCreateRequest {
  connectionId: Id;
  projectId: Id;
  missionId?: Id;
  prompt: string;
  baseRef?: string;
  model?: string;
  budget?: CloudBudget;
  requirePlanApproval?: boolean;
}

/**
 * POST /api/agents-cloud/connections. The credential goes to secrets first; only its id comes
 * here, and the server checks the person may use that secret, as for a CI connection.
 */
export interface CloudAgentConnectRequest {
  provider: CloudAgentProviderId;
  title?: string;
  projectIds: Id[];
  secretRef?: string;
  endpoint?: string;
  machineId?: Id;
  environmentId?: string;
}

/** POST /api/agents-cloud/runs/:id/message */
export interface CloudRunMessage {
  text: string;
}

/** A connection as state.ext['agents-cloud'] shows it: no secret ids (as CiConnectionSummary). */
export type CloudAgentConnectionSummary = Omit<CloudAgentConnection, 'secretRef'>;

/** state.ext['agents-cloud'] and GET /api/agents-cloud */
export interface CloudAgentsState {
  connections: CloudAgentConnectionSummary[];
  runs: CloudRun[];
}

/** Poll every 30 seconds while a run is active, every 5 minutes while idle, backing off on 429. */
export const CLOUD_POLL_ACTIVE_MS = 30_000;
export const CLOUD_POLL_IDLE_MS = 5 * 60_000;

// ---------------------------------------------------------------------------
// Choosing an agent
// ---------------------------------------------------------------------------

/** What a mission or template asks for: a kind (local or cloud) and optional model, or 'auto' by policy. */
export type MissionAgentChoice = { kind: AgentKind; model?: string } | 'auto';

/** A kind a machine or the server can run, for pickers: local kinds from runtimes, cloud kinds from connections. */
export interface AgentKindOption {
  kind: AgentKind;
  label: string;
  where: 'machine' | 'cloud';
  /** Local: installed and signed in on at least one online machine. Cloud: a connection exists and policy allows it. */
  available: boolean;
  /** Why not, when unavailable: what to do next. */
  why?: string;
}
